/**
 * FMNV Contact Form — Client-Side Logic
 *
 * Verantwortlichkeiten:
 *  - Conditional-Fields je Kategorie ein-/ausblenden
 *  - File-Upload-Validierung (Anzahl, Größe, Typ)
 *  - Honeypot-Check vor Submit
 *  - Consent-Check vor Submit
 *  - Turnstile-Token-Check vor Submit
 *  - Auto-Scroll + Focus bei Success-Block
 *
 * Progressive Enhancement: ohne JS bleibt das Formular funktional (alle Felder
 * sichtbar, native HTML-Validierung). Mit JS: UX-Verbesserungen + Anti-Spam.
 */

(function () {
  'use strict';

  const MAX_FILES = 5;
  const MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10 MB in Bytes
  const ALLOWED_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
    'application/pdf'
  ];
  const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

  class FmnvContactForm {
    constructor(formEl) {
      this.form = formEl;
      this.categorySelect = formEl.querySelector('[data-fmnv-category]');
      this.categoryGroups = formEl.querySelectorAll('[data-fmnv-category-group]');
      this.fileInput = formEl.querySelector('[data-fmnv-file-input]');
      this.fileList = formEl.querySelector('[data-fmnv-file-list]');
      this.fileCounter = formEl.querySelector('[data-fmnv-file-counter]');
      this.fileUploadZone = formEl.querySelector('[data-fmnv-file-upload]');
      this.consentCheckbox = formEl.querySelector('[data-fmnv-consent]');
      this.submitButton = formEl.querySelector('[data-fmnv-submit]');
      this.honeypot = formEl.querySelector('[data-fmnv-honeypot]');
      this.turnstileContainer = formEl.querySelector('[data-fmnv-turnstile]');

      this.locales = this.parseLocales(formEl);

      this.init();
    }

    parseLocales(formEl) {
      const script = formEl.querySelector('[data-fmnv-locales]');
      if (!script) return {};
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.warn('FMNV Contact: Failed to parse locales', e);
        return {};
      }
    }

    t(key, fallback) {
      return this.locales[key] || fallback || key;
    }

    init() {
      if (this.categorySelect) {
        this.categorySelect.addEventListener('change', () => this.onCategoryChange());
        // Initial state
        this.onCategoryChange();
      }

      if (this.fileInput) {
        this.fileInput.addEventListener('change', (e) => this.onFileInputChange(e));
        this.initDragDrop();
      }

      this.form.addEventListener('submit', (e) => this.onSubmit(e));

      // Lazy-load Turnstile script on first focus if container exists
      if (this.turnstileContainer) {
        this.lazyLoadTurnstile();
      }

      // Auto-scroll to success block on page load (after redirect from Shopify)
      this.handleSuccessScroll();
    }

    // ------------------------------------------------------------
    // CONDITIONAL FIELDS
    // ------------------------------------------------------------

    onCategoryChange() {
      const selectedValue = this.categorySelect.value;

      this.categoryGroups.forEach((group) => {
        const targetCategory = group.getAttribute('data-fmnv-category-group');
        const isVisible = targetCategory === selectedValue;
        this.setGroupVisibility(group, isVisible);
      });

      // Focus first newly visible field for accessibility
      const firstVisibleInput = this.form.querySelector(
        '.fmnv-category-group.is-visible input:not([type="hidden"]), .fmnv-category-group.is-visible textarea'
      );
      if (firstVisibleInput && document.activeElement === this.categorySelect) {
        // Only shift focus if user interacted (not on initial load)
        // Skipping focus on initial render to avoid jumping
      }
    }

    setGroupVisibility(group, visible) {
      if (visible) {
        group.classList.add('is-visible');
        group.removeAttribute('hidden');
        group.setAttribute('aria-hidden', 'false');
        this.toggleRequiredInputs(group, true);
      } else {
        group.classList.remove('is-visible');
        group.setAttribute('hidden', '');
        group.setAttribute('aria-hidden', 'true');
        this.toggleRequiredInputs(group, false);
      }
    }

    toggleRequiredInputs(group, enable) {
      // Re-apply required attribute based on data-fmnv-required-in-group
      // This allows conditional required fields
      group.querySelectorAll('[data-fmnv-required-in-group]').forEach((input) => {
        if (enable) {
          input.setAttribute('required', '');
          input.setAttribute('aria-required', 'true');
        } else {
          input.removeAttribute('required');
          input.removeAttribute('aria-required');
        }
      });
    }

    // ------------------------------------------------------------
    // FILE UPLOAD
    // ------------------------------------------------------------

    onFileInputChange(event) {
      const files = Array.from(event.target.files || []);
      this.validateAndRenderFiles(files);
    }

    initDragDrop() {
      if (!this.fileUploadZone) return;

      ['dragenter', 'dragover'].forEach((evt) => {
        this.fileUploadZone.addEventListener(evt, (e) => {
          e.preventDefault();
          this.fileUploadZone.classList.add('is-dragover');
        });
      });

      ['dragleave', 'drop'].forEach((evt) => {
        this.fileUploadZone.addEventListener(evt, (e) => {
          e.preventDefault();
          this.fileUploadZone.classList.remove('is-dragover');
        });
      });

      this.fileUploadZone.addEventListener('drop', (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files || []);
        // Write the dropped files into the file input (via DataTransfer)
        const dt = new DataTransfer();
        droppedFiles.forEach((f) => dt.items.add(f));
        this.fileInput.files = dt.files;
        this.validateAndRenderFiles(droppedFiles);
      });
    }

    validateAndRenderFiles(files) {
      this.clearError('files');
      if (!this.fileList) return;

      const errors = [];
      let totalSize = 0;
      const validFiles = [];

      if (files.length > MAX_FILES) {
        errors.push(this.t('fmnv_contact.errors.files_too_many', `Max ${MAX_FILES} files.`));
      }

      files.slice(0, MAX_FILES).forEach((file) => {
        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
          errors.push(
            this.t(
              'fmnv_contact.errors.file_type_invalid',
              'Invalid file type: ' + file.name
            ) + ` (${file.name})`
          );
          return;
        }
        totalSize += file.size;
        validFiles.push(file);
      });

      if (totalSize > MAX_TOTAL_SIZE) {
        errors.push(this.t('fmnv_contact.errors.files_too_large', 'Files too large.'));
      }

      this.renderFileList(validFiles, errors, totalSize);

      if (errors.length > 0) {
        this.showFileError(errors[0]);
        // Reset the input — do not submit invalid files
        this.fileInput.value = '';
      }
    }

    renderFileList(files, errors, totalSize) {
      this.fileList.innerHTML = '';

      files.forEach((file, index) => {
        const li = document.createElement('li');
        li.className = 'fmnv-file-list__item';
        li.setAttribute('aria-label', `${file.name}, ${this.formatBytes(file.size)}`);

        const name = document.createElement('span');
        name.textContent = `✓ ${file.name} (${this.formatBytes(file.size)})`;

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'fmnv-file-list__remove';
        remove.setAttribute('aria-label', `Remove ${file.name}`);
        remove.textContent = '×';
        remove.addEventListener('click', () => this.removeFile(index));

        li.appendChild(name);
        li.appendChild(remove);
        this.fileList.appendChild(li);
      });

      if (this.fileCounter) {
        this.fileCounter.textContent =
          `${files.length} / ${MAX_FILES} · ${this.formatBytes(totalSize)} / ${this.formatBytes(MAX_TOTAL_SIZE)}`;
      }
    }

    removeFile(indexToRemove) {
      const currentFiles = Array.from(this.fileInput.files || []);
      const dt = new DataTransfer();
      currentFiles.forEach((f, i) => {
        if (i !== indexToRemove) dt.items.add(f);
      });
      this.fileInput.files = dt.files;
      this.validateAndRenderFiles(Array.from(dt.files));
    }

    showFileError(message) {
      const errorEl = this.form.querySelector('[data-fmnv-error="files"]');
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
    }

    formatBytes(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    // ------------------------------------------------------------
    // TURNSTILE LAZY-LOAD
    // ------------------------------------------------------------

    lazyLoadTurnstile() {
      // Load Turnstile script on first form interaction for performance
      let loaded = false;
      const loadScript = () => {
        if (loaded) return;
        loaded = true;
        const script = document.createElement('script');
        script.src = TURNSTILE_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      };

      // Trigger on first focus or interaction
      const events = ['focusin', 'click', 'touchstart'];
      const handler = () => {
        loadScript();
        events.forEach((evt) => this.form.removeEventListener(evt, handler));
      };
      events.forEach((evt) => this.form.addEventListener(evt, handler, { once: true, passive: true }));
    }

    // ------------------------------------------------------------
    // SUBMIT HANDLING
    // ------------------------------------------------------------

    onSubmit(event) {
      // 1. Honeypot check — silent fail if bot filled it
      if (this.honeypot && this.honeypot.value !== '') {
        event.preventDefault();
        return;
      }

      // 2. Consent check
      if (this.consentCheckbox && !this.consentCheckbox.checked) {
        event.preventDefault();
        this.showError('fmnv_contact.errors.consent_required', 'consent');
        this.consentCheckbox.focus();
        return;
      }
      this.clearError('consent');

      // 3. Turnstile token check (only if widget exists)
      if (this.turnstileContainer) {
        const tokenInput = this.form.querySelector('[name="cf-turnstile-response"]');
        const token = tokenInput ? tokenInput.value : '';
        if (!token) {
          event.preventDefault();
          this.showError('fmnv_contact.errors.turnstile_required', 'turnstile');
          return;
        }
        this.clearError('turnstile');
      }

      // 4. Show loading state on submit button
      if (this.submitButton) {
        this.submitButton.classList.add('is-loading');
        this.submitButton.disabled = true;
        const loadingText = this.t('fmnv_contact.labels.submit_sending', 'Sending...');
        const originalText = this.submitButton.dataset.originalLabel || this.submitButton.textContent;
        if (!this.submitButton.dataset.originalLabel) {
          this.submitButton.dataset.originalLabel = originalText;
        }
        this.submitButton.textContent = loadingText;
      }

      this.form.setAttribute('aria-busy', 'true');
      // Note: we do NOT preventDefault — the form submits natively to Shopify
    }

    handleSuccessScroll() {
      const successBlock = document.querySelector('[data-fmnv-success]');
      if (!successBlock) return;

      // Smooth scroll and focus the success block
      requestAnimationFrame(() => {
        successBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
        successBlock.setAttribute('tabindex', '-1');
        successBlock.focus({ preventScroll: true });
      });
    }

    // ------------------------------------------------------------
    // UTILITIES
    // ------------------------------------------------------------

    showError(key, fieldName) {
      const errorEl = this.form.querySelector(`[data-fmnv-error="${fieldName}"]`);
      if (errorEl) {
        errorEl.textContent = this.t(key, errorEl.textContent);
        errorEl.hidden = false;
      }
    }

    clearError(fieldName) {
      const errorEl = this.form.querySelector(`[data-fmnv-error="${fieldName}"]`);
      if (errorEl) {
        errorEl.hidden = true;
      }
    }
  }

  // ------------------------------------------------------------
  // AUTO-INIT on DOMContentLoaded
  // ------------------------------------------------------------

  function initAll() {
    document.querySelectorAll('[data-fmnv-contact-form]').forEach((formEl) => {
      if (!formEl.dataset.fmnvInitialized) {
        new FmnvContactForm(formEl);
        formEl.dataset.fmnvInitialized = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
